import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BarChartComponent from "../../components/results/barChartComponent";
import stressAnalyticsApi from "../../api/stressAnalyticsApi";
import usersApi from "../../api/usersApi";               // ← para getEstresResponses
import companiesApi from "../../api/companiesApi";       // ← para getCompanies
import responsesApi from "../../api/responsesApi";       // ← para deleteEstresResponse

import jobAreaList from "../../data/jobArea";
import ageRanges from "../../data/ageRanges";
import mexicoStates from "../../data/mexicoStates";
import biologicalSexList from "../../data/biologicalSex";
import styles from "./AnalyticsPage.module.css";

const formatDate = (iso) => {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const StressAnalyticsGlobalPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // filtros de gráficas
  const [selectedFilters, setSelectedFilters] = useState({
    ageRange:       searchParams.get("ageRange")       || "",
    residenceState: searchParams.get("residenceState") || "",
    biologicalSex:  searchParams.get("biologicalSex")  || "",
    companyId:      searchParams.get("companyId")      || ""  // ← añadido
  });

  // estados para analytics
  const [analyticsData, setAnalyticsData] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  // estados para empresas y respuestas
  const [companies, setCompanies] = useState([]);
  const [responsesList, setResponsesList] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(true);

  // handler filtros URL
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...selectedFilters, [name]: value };
    setSelectedFilters(newFilters);

    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(name, value);
    else newParams.delete(name);
    setSearchParams(newParams);
  };

  // fetch analytics de estrés
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const data = await stressAnalyticsApi.getStressGlobalAnalytics(selectedFilters);
        if (initialTotal === null && data?.totalRespondents) {
          setInitialTotal(data.totalRespondents);
        }
        setAnalyticsData(data);
      } catch (error) {
        console.error("Error cargando datos analíticos de estrés:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedFilters]);

  // cargar lista de empresas
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await companiesApi.getCompanies();
        setCompanies(data);
      } catch (err) {
        console.error("Error cargando empresas:", err);
      }
    };
    fetchCompanies();
  }, []);

  // cargar respuestas de estrés al cambiar companyId
  useEffect(() => {
    const fetchResponses = async () => {
      setLoadingResponses(true);
      try {
        const data = await usersApi.getEstresResponses(selectedFilters.companyId);
        setResponsesList(data.responses);
      } catch (err) {
        console.error("Error cargando respuestas de estrés:", err);
      } finally {
        setLoadingResponses(false);
      }
    };
    fetchResponses();
  }, [selectedFilters.companyId]);

  // handler para borrar una respuesta de estrés
  const handleDelete = async (responseId) => {
    try {
      await responsesApi.deleteEstresResponse(responseId);
      setResponsesList(prev => prev.filter(r => r.responseId !== responseId));
      alert("✅ La respuesta de estrés ha sido eliminada correctamente.");
    } catch (err) {
      console.error("Error eliminando respuesta de estrés:", err);
      alert("⚠️ Ocurrió un error al eliminar la respuesta de estrés.");
    }
  };

  const renderFilter = (label, name, options) => (
    <div className={styles.filterItem} key={name}>
      <label htmlFor={name} className={styles.filterLabel}>{label}</label>
      <select
        id={name}
        name={name}
        value={selectedFilters[name]}
        onChange={handleFilterChange}
        className={styles.select}
      >
        <option value="">Todos</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className={styles.page}>
      {initialTotal !== null && (
        <div className={styles.initialTotalCapsule}>
          Total inicial: <span>{initialTotal}</span>
        </div>
      )}

      <header className={styles.header}>
        <h2>Gráficas Generales de Estrés Laboral</h2>
        <p className={styles.subheader}>Global overview across all companies</p>
      </header>

      <section className={styles.filters}>
        {renderFilter("Rango de Edad", "ageRange", ageRanges)}
        {renderFilter("Estado de Residencia", "residenceState", mexicoStates)}
        {renderFilter("Sexo Biológico", "biologicalSex", biologicalSexList)}
      </section>

      {!loading && analyticsData && (
        <div className={styles.totalCapsule}>
          Total encuestados: <span>{analyticsData.totalRespondents}</span>
        </div>
      )}

      <section className={styles.content}>
        {loading ? (
          <p className={styles.message}>Cargando gráficas globales...</p>
        ) : analyticsData ? (
          <div className={styles.charts}>
            <BarChartComponent data={analyticsData.hasBurnOut} title="Presencia de Burnout" />
            <BarChartComponent data={analyticsData.exhaustionLevel} title="Agotamiento Emocional" />
            <BarChartComponent data={analyticsData.cynicismLevel} title="Desconexión Laboral" />
            <BarChartComponent data={analyticsData.efficacyLevel} title="Eficiencia Laboral" />
            <BarChartComponent data={analyticsData.physicalStress} title="Estrés Físico" />
          </div>
        ) : (
          <p className={styles.message}>No se pudieron cargar los datos.</p>
        )}
      </section>

      <hr className={styles.separator} />

      <section className={styles.filters}>
        <div className={styles.filterItem}>
          <label htmlFor="companyId" className={styles.filterLabel}>
            Empresa
          </label>
          <select
            id="companyId"
            name="companyId"
            value={selectedFilters.companyId}
            onChange={handleFilterChange}
            className={styles.select}
          >
            <option value="">Todas</option>
            {companies.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
      </section>

      <section className={styles.responsesSection}>
        {loadingResponses ? (
          <p className={styles.message}>Cargando respuestas...</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Cuestionario</th>
                <th>Fecha</th>
                <th>Borrar</th>
              </tr>
            </thead>
            <tbody>
              {responsesList.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.message}>
                    No hay respuestas para mostrar.
                  </td>
                </tr>
              ) : (
                responsesList.map(r => (
                  <tr key={r.responseId}>
                    <td>{r.username}</td>
                    <td>{r.questionnaireName}</td>
                    <td>{formatDate(r.timestamp)}</td>
                    <td>
                      <button
                        onClick={() => handleDelete(r.responseId)}
                        className={styles.deleteButton}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default StressAnalyticsGlobalPage;
